import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { rawMaterialAPI, warehouseAPI } from '../utils/api';
import { FlaskConical, Plus, Edit2, X, AlertTriangle, TrendingUp, TrendingDown, Search, Trash2, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { Thumb, ImagePicker } from '../components/common/ImageZoom';
import StatusFilter from '../components/common/StatusFilter';
import { localName, fmt, apiError, STOCK_BADGE } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';

const emptyForm = { name: '', name_en: '', name_ru: '', unit: '', quantity: 0, warehouse_id: '', low_stock_threshold: 0, description: '' };

export default function RawMaterials() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user } = useAuthStore();
  const canEdit = ['admin', 'manager', 'production_manager'].includes(user?.role);

  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjustWh, setAdjustWh] = useState('');
  const [warehouses, setWarehouses] = useState([]);

  const load = () => {
    setLoading(true);
    rawMaterialAPI.list().then(r => setMaterials(r.data)).catch(() => toast.error(t('error'))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); warehouseAPI.list().then(r => setWarehouses(r.data)).catch(() => {}); }, []); // eslint-disable-line

  const q = search.trim().toLowerCase();
  const filtered = materials.filter(m =>
    (!q || [m.name, m.name_en, m.name_ru, m.description, m.unit].some(v => (v || '').toLowerCase().includes(q))) &&
    (statusFilter === 'all' || m.stock_status === statusFilter)
  );
  const lowCount = materials.filter(m => m.stock_status !== 'in_stock').length;
  const counts = { all: materials.length, in_stock: 0, low: 0, out: 0 };
  materials.forEach(m => { counts[m.stock_status] = (counts[m.stock_status] || 0) + 1; });

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, warehouse_id: warehouses[0]?.id || '' }); setImage(null); setShowModal(true); };
  const openEdit = m => {
    setEditing(m);
    setForm({ name: m.name, name_en: m.name_en || '', name_ru: m.name_ru || '', unit: m.unit, quantity: m.quantity,
      low_stock_threshold: m.low_stock_threshold, description: m.description || '' });
    setImage(m.image_path || null);
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditing(null); setForm(emptyForm); setImage(null); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.unit.trim()) return toast.error(t('name_unit_required'));
    setSaving(true);
    try {
      let id = editing?.id;
      const { quantity, warehouse_id, ...rest } = form;
      if (editing) await rawMaterialAPI.update(id, rest);
      else id = (await rawMaterialAPI.create({ ...rest, quantity, warehouse_id: warehouse_id || null })).data.id;
      if (image instanceof File) await rawMaterialAPI.uploadImage(id, image);
      else if (editing?.image_path && !image) await rawMaterialAPI.removeImage(id);
      toast.success(t('saved'));
      closeModal(); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const handleDelete = async m => {
    if (!window.confirm(t('confirm_delete'))) return;
    try { await rawMaterialAPI.delete(m.id); toast.success(t('deleted')); load(); } catch (e) { toast.error(apiError(e, t)); }
  };

  const handleAdjust = async () => {
    const qty = parseFloat(adjustQty);
    if (isNaN(qty) || qty === 0) return toast.error(t('invalid_quantity'));
    setSaving(true);
    try {
      await rawMaterialAPI.adjust(adjustModal.id, { quantity: qty, notes: adjustNotes, warehouse_id: adjustWh || null });
      toast.success(t('saved')); setAdjustModal(null); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <SectionHero title={t('raw_materials')} subtitle={<>{materials.length} {t('raw_materials')}{lowCount > 0 ? ` · ${lowCount} ` + t('low_stock') : ''}</>}>
        {canEdit && <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> {t('add_material')}</button>}
      </SectionHero>

      {lowCount > 0 && <div className="alert alert-warning"><AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} /><span>{lowCount} {t('low_stock_alert')}</span></div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 200px' }}>
            <Search size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input className="input" style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 13 }} placeholder={t('search') + '...'} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <StatusFilter value={statusFilter} onChange={setStatusFilter} counts={counts} t={t} />
        </div>

        {loading ? <div className="loading-center"><div className="spinner" /></div>
          : filtered.length === 0 ? <div className="empty-state"><FlaskConical size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th style={{ width: 56 }}>{t('image')}</th>
                  <th>{t('name')}</th>
                  <th>{t('quantity')}</th>
                  <th className="hide-mobile">{t('low_stock_threshold')}</th>
                  <th>{t('status')}</th>
                  {canEdit && <th>{t('actions')}</th>}
                </tr></thead>
                <tbody>
                  {filtered.map(m => (
                    <tr key={m.id}>
                      <td><Thumb src={m.image_path} alt={m.name} /></td>
                      <td>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(m, lang)}</div>
                        {m.description && <div className="text-xs truncate" style={{ color: 'var(--text-muted)', maxWidth: 260 }} title={m.description}>{m.description}</div>}
                      </td>
                      <td style={{ fontFamily: 'monospace' }}>{fmt(m.quantity)} <span style={{ color: 'var(--text-muted)' }}>{m.unit}</span>
                        {m.stocks.filter(x => x.quantity).length > 1 && <div className="text-xs" style={{ color: 'var(--text-muted)', fontFamily: 'inherit' }}>{m.stocks.filter(x => x.quantity).map(x => `${x.warehouse_name}: ${fmt(x.quantity)}`).join(' · ')}</div>}
                        {m.stocks.filter(x => x.quantity).length === 1 && <div className="text-xs" style={{ color: 'var(--text-muted)', fontFamily: 'inherit' }}>{m.stocks.find(x => x.quantity).warehouse_name}</div>}
                        {m.in_transit > 0 && <Link to="/shipments" className="transit-chip" title={t('sh_in_transit_hint')}><Truck size={11} /> +<bdi>{fmt(m.in_transit)}</bdi> {m.unit} {t('sh_on_the_road_short')}</Link>}</td>
                      <td className="hide-mobile" style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{fmt(m.low_stock_threshold)}</td>
                      <td><span className={`badge ${STOCK_BADGE[m.stock_status]}`}>{t('stock_' + m.stock_status)}</span></td>
                      {canEdit && (
                        <td>
                          <div className="flex items-center gap-1">
                            <button className="btn btn-ghost btn-sm" onClick={() => { setAdjustModal(m); setAdjustQty(''); setAdjustNotes(''); setAdjustWh((m.stocks.slice().sort((a, b) => b.quantity - a.quantity)[0] || {}).warehouse_id || warehouses[0]?.id || ''); }}><TrendingUp size={13} /> {t('adjust')}</button>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(m)}><Edit2 size={13} /></button>
                            <button className="btn btn-ghost btn-sm btn-icon" style={{ color: '#f87171' }} onClick={() => handleDelete(m)}><Trash2 size={13} /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{editing ? t('edit_material') : t('add_material')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('name')} *</label><input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">{t('unit')} *</label><input className="input" value={form.unit} placeholder="kg, ton, m" onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('name_en')}</label><input className="input" dir="ltr" value={form.name_en} onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">{t('name_ru')}</label><input className="input" dir="ltr" value={form.name_ru} onChange={e => setForm(f => ({ ...f, name_ru: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                {!editing && <div className="form-group"><label className="form-label">{t('initial_quantity')}</label>
                  <input className="input" type="number" min="0" step="any" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 0 }))} /></div>}
                {!editing && form.quantity > 0 && <div className="form-group"><label className="form-label">{t('warehouse')}</label>
                  <select className="select" value={form.warehouse_id} onChange={e => setForm(f => ({ ...f, warehouse_id: e.target.value }))}>
                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>}
                <div className="form-group"><label className="form-label">{t('low_stock_threshold')}</label>
                  <input className="input" type="number" min="0" step="any" value={form.low_stock_threshold} onChange={e => setForm(f => ({ ...f, low_stock_threshold: parseFloat(e.target.value) || 0 }))} /></div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('description')}</label>
                <textarea className="input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ minHeight: 64 }} />
              </div>
              <ImagePicker label={t('image')} value={image} onChange={setImage} onRemove={() => setImage(null)} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('loading') : t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {adjustModal && (
        <div className="modal-overlay" onClick={() => setAdjustModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{t('adjust_stock')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setAdjustModal(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 12, background: 'rgba(0,0,0,0.25)', border: '1px solid var(--border)' }}>
                <p style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(adjustModal, lang)}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{t('current_stock')}: <strong>{fmt(adjustModal.quantity)} {adjustModal.unit}</strong></p>
              </div>
              <div className="form-group">
                <label className="form-label">{t('warehouse')}</label>
                <select className="select" value={adjustWh} onChange={e => setAdjustWh(e.target.value)}>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name} ({fmt(adjustModal.stocks.find(x => x.warehouse_id === w.id)?.quantity || 0)} {adjustModal.unit})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('quantity_change')}</label>
                <input className="input" type="number" step="any" placeholder="+100 / -50" value={adjustQty} onChange={e => setAdjustQty(e.target.value)} />
                {adjustQty && !isNaN(parseFloat(adjustQty)) && (
                  <p className="text-xs mt-1 flex items-center gap-1" style={{ color: parseFloat(adjustQty) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {parseFloat(adjustQty) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {t('new_total')}: {fmt((adjustModal.stocks.find(x => x.warehouse_id === adjustWh)?.quantity || 0) + (parseFloat(adjustQty) || 0))} {adjustModal.unit}
                  </p>
                )}
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('notes')}</label>
                <input className="input" value={adjustNotes} onChange={e => setAdjustNotes(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setAdjustModal(null)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleAdjust} disabled={saving}>{saving ? t('loading') : t('apply')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
